import ListIterator = _.ListIterator;
import MemoIterator = _.MemoIterator;

//this is often fed into the reduce function which is why it is defined in here.
export function sum(a: number, b: number): number {
    return a + b;
}

export function addOrRemove<T>(arr: T[], newLength: number, insertFn: () => T) : void {
    const diff = newLength - arr.length;
    if (diff > 0) {
        for (let i = 0; i < diff; i++) {
            arr.push(insertFn());
        }
    }
    if (diff < 0) {
        arr.splice(arr.length + diff, -diff);
    }
}

export function indexOf<T>(arr: T[], matchFn: ListIterator<T, boolean>) : number {
    return _.findIndex<T>(arr, matchFn);
}

export function enqueue<T>(item: T, array: T[], max: number): T[] {
    const l = array.unshift(item);
    if (l > max) {
        array.splice(array.length - 1, 1);
    }
    return array;
}

export function numberArray(from: number, to: number): number[] {
    const arr: number[] = [];
    if (from > to) {
        for (let i = from; i >= to; i--) {
            arr.push(i);
        }
    } else {
        for (let i = from; i <= to; i++) {
            arr.push(i);
        }
    }
    return arr;
}

export function map<T, R>(arr: T[], func: ListIterator<T, R>) : R[]{
    return _.map(arr, func);
}

export function filter<T>(array: T[], predicate: ListIterator<T, boolean>): T[] {
    return _.filter(array, predicate);
}

export function first<T>(arr: T[], func: ListIterator<T, boolean>): T {
    return _.head(_.filter(arr, func));
}

export function any<T>(arr: T[], func: ListIterator<T, boolean>) : boolean {
    return _.some(arr, func);
}

export function count<T>(arr: T[], func: ListIterator<T, boolean>): number {
    return _.filter(arr, func).length;
}

export function reduce<T,R>(fn: MemoIterator<T,R>, arr: T[], agg?: R) : R {
    return agg ? _.reduce(arr, fn, agg) : _.reduce(arr, fn);
}

export function sort<T>(arr: T[], key: string) : T[] {
    return arr.sort((a: T, b: T) => {
        const x = a[key];
        const y = b[key];
        return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
}

export function toDict<T>(arr: T[], keyfn: (T: T) => any, valfn?: (T: T) => any) {
    return _.reduce(arr, (agg, x) => {
        agg[keyfn(x)] = valfn ? valfn(x) : x;
        return agg;
    }, {});
}

export function createRange(maxValue, initialValue) {
    if (isNaN(parseInt(maxValue, 10))) {
        return [];
    }

    const init = parseInt(initialValue, 10);
    const start = isNaN(init) ? 1 : init;
    const arr:any = [];

    for (let i = start; i <= maxValue; i++) {
        arr.push(i);
    }

    return arr;
}

export function chunk(obj: string, chunkSize: number) : string[]  {
    const arr:any = [];
    for (let i = 0; i < obj.length; i += chunkSize) {
        arr.push(obj.slice(i, i + chunkSize));
    }

    return arr;
}

export function removeDuplicates<T>(arr: T[]) : T[] {
    const filtered: T[] = [];
    arr.forEach((val: T) => {
        if (filtered.indexOf(val) === -1) {
            filtered.push(val);
        }
    });
    return filtered;
}

export function mapToInt(numbers: string[]): number[] {
    return _.map(numbers, n => parseInt(n, 10));
}

export function wrapNonArray<T>(valOrArray: T|T[]|null|undefined) : T[] {
    // Normalise single vals and arrays e.g. for `forEach()`
    // An empty array is returned if `null` or `undefined` is passed in
    return (valOrArray == null) ? [] : _.flatten([valOrArray]);
}

export function difference<T>(arr1: T[], arr2: T[], predicate?: (a: T, b: T) => boolean) : T[] {
    return _.filter(arr1, function (a) {
        return !_.find(arr2, function (b) {
            return predicate === undefined
                ? angular.equals(a, b)
                : predicate(a, b);
        });
    });
}

export function promiseSeries(fnArray) {
    // Run an array of functions as `then` statements in series
    // e.g. http requests one after the other, not in parallel
    return fnArray.reduce(
        (promise, fn) => promise.then(res => fn(res)),
        Promise.resolve()
    );
}
